import type { Doc } from "building-types/doc";
import { HorizontalFloorAssemblyType } from "building-types/floors/floorAssemblyTypes";
import type { SolidFloorAssemblyOverride } from "building-types/floors/floorAssemblyTypes";
import type { ForeignEntityId } from "building-types/handles/foreignEntityId";
import { allRoomsModeRefProps } from "./floorAssembly";
import { buildFloorRoomRefMergeProps } from "./floorRoomRefTools";

/** Default display name for room-derived floor assemblies. */
export const ROOM_DERIVED_FLOOR_NAME_BASE = "Room-Derived Floor";
/** Default display name for freeform floor assemblies. */
export const FREEFORM_FLOOR_NAME_BASE = "Freeform Floor";
/** Pre-rename freeform default; still treated as auto-generated when converting modes. */
export const LEGACY_FREEFORM_FLOOR_NAME_BASE = "Free Form Floor";

export function defaultFloorNameForHorizontalAssemblyType(
  type: HorizontalFloorAssemblyType.FREE_FORM | HorizontalFloorAssemblyType.ROOM_DERIVED,
): string {
  return type === HorizontalFloorAssemblyType.ROOM_DERIVED ? ROOM_DERIVED_FLOOR_NAME_BASE : FREEFORM_FLOOR_NAME_BASE;
}

function isNameWithOptionalCount(name: string, base: string): boolean {
  return name === base || new RegExp(`^${base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")} \\d+$`).test(name);
}

/** True when the floor still has an auto-generated freeform/room-derived name. */
export function isDefaultHorizontalAssemblyFloorName(name: string | undefined): boolean {
  if (!name) {
    return false;
  }
  return (
    isNameWithOptionalCount(name, ROOM_DERIVED_FLOOR_NAME_BASE) ||
    isNameWithOptionalCount(name, FREEFORM_FLOOR_NAME_BASE) ||
    isNameWithOptionalCount(name, LEGACY_FREEFORM_FLOOR_NAME_BASE)
  );
}

export function clearFreeformShapeProps(): Partial<SolidFloorAssemblyOverride> {
  return {
    perimeters: undefined,
    edgeIds: undefined,
    perimeterEdgeCounts: undefined,
    floorEdgeOverrides: undefined,
    openings: undefined,
    openingEdgeIds: undefined,
    openingEdgeCounts: undefined,
    floorOpeningEdgeOverrides: undefined,
    splitData: undefined,
  };
}

export function clearRoomDerivedRefProps(): Partial<SolidFloorAssemblyOverride> {
  return {
    allRoomsOnLevel: undefined,
    excludedRoomIds: undefined,
    roomIds: undefined,
    splitData: undefined,
  };
}

export function roomDerivedConversionProps(
  doc: Doc,
  floor: SolidFloorAssemblyOverride,
  levelId: string | undefined,
  roomIdsAtLevel: readonly ForeignEntityId<"ROOM">[],
): Partial<SolidFloorAssemblyOverride> {
  const roomRefProps = allRoomsModeRefProps(levelId);
  const nextFloor = {
    ...floor,
    horizontalAssemblyType: HorizontalFloorAssemblyType.ROOM_DERIVED,
    ...roomRefProps,
  } as SolidFloorAssemblyOverride;

  return {
    horizontalAssemblyType: HorizontalFloorAssemblyType.ROOM_DERIVED,
    ...roomRefProps,
    ...clearFreeformShapeProps(),
    ...buildFloorRoomRefMergeProps(doc, nextFloor, roomIdsAtLevel, roomRefProps),
  };
}

export function freeformConversionProps(): Partial<SolidFloorAssemblyOverride> {
  return {
    horizontalAssemblyType: HorizontalFloorAssemblyType.FREE_FORM,
    ...clearRoomDerivedRefProps(),
  };
}
